<template>
  <div class="mb-6 w-full">
    <div class="overflow-hidden bg-white rounded-lg shadow-md dark:bg-gray-800">
      <!-- Header with Toggle -->
      <div
        @click="isExpanded = !isExpanded"
        class="flex justify-between items-center p-4 transition-colors cursor-pointer sm:p-6 hover:bg-gray-50 dark:hover:bg-gray-700">
        <div class="flex gap-3 items-center">
          <svg
            class="w-5 h-5 text-blue-600 dark:text-blue-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
          </svg>
          <h2 class="text-lg font-bold text-gray-800 sm:text-xl dark:text-gray-100">
            User Filters
          </h2>
          <span
            v-if="hasActiveFilters"
            class="px-2 py-1 text-xs font-semibold text-blue-600 bg-blue-100 rounded-full dark:text-blue-400 dark:bg-blue-900/30">
            {{ activeFilterCount }}
          </span>
        </div>
        <svg
          :class="['w-5 h-5 text-gray-600 dark:text-gray-400 transition-transform duration-200', { 'rotate-180': isExpanded }]"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
        </svg>
      </div>

      <!-- Collapsible Content -->
      <div
        :class="['transition-all duration-300 ease-in-out overflow-hidden', isExpanded ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0']">
        <div class="px-4 py-4 space-y-4 sm:px-6 sm:pb-6">
          <!-- Search Input -->
          <div>
            <label for="search" class="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
              Search
            </label>
            <input
              id="search"
              type="text"
              v-model="search"
              placeholder="Search by name or email..."
              class="px-4 py-2.5 w-full placeholder-gray-400 text-gray-900 bg-white rounded-lg border border-gray-300 transition outline-none sm:py-2 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 dark:placeholder-gray-500 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent" />
          </div>

          <!-- Role Select Dropdown -->
          <div>
            <label for="role" class="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
              Role
            </label>
            <select
              id="role"
              v-model="selectedRole"
              class="px-4 py-2.5 w-full text-gray-900 bg-white rounded-lg border border-gray-300 transition outline-none sm:py-2 dark:bg-gray-700 dark:text-gray-100 dark:border-gray-600 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent">
              <option v-for="role in roles" :key="role.value" :value="role.value">
                {{ role.label }}
              </option>
            </select>
          </div>

          <!-- Bank Account Checkbox -->
          <div class="flex items-center py-2">
            <input
              id="bankAccount"
              type="checkbox"
              v-model="hasBankAccount"
              class="w-5 h-5 text-blue-600 bg-white rounded border-gray-300 sm:w-4 sm:h-4 dark:text-blue-500 dark:border-gray-600 dark:bg-gray-700 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400" />
            <label for="bankAccount" class="ml-3 text-sm font-medium text-gray-700 dark:text-gray-300">
              Has Bank Account
            </label>
          </div>

          <!-- Action Buttons -->
          <div class="flex flex-col gap-3 pt-2 sm:flex-row">
            <button
              @click="handleSearch"
              class="flex-1 px-6 py-3 font-medium text-white bg-blue-600 rounded-lg transition duration-200 sm:py-2.5 dark:bg-blue-500 hover:bg-blue-700 dark:hover:bg-blue-600 active:scale-95 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:ring-offset-2 dark:focus:ring-offset-gray-800">
              Search
            </button>
            <button
              @click="handleReset"
              class="px-6 py-3 font-medium text-gray-700 rounded-lg border border-gray-300 transition duration-200 sm:py-2.5 dark:text-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 active:scale-95 focus:outline-none focus:ring-2 focus:ring-gray-500 dark:focus:ring-gray-400 focus:ring-offset-2 dark:focus:ring-offset-gray-800">
              Reset
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
const search = ref('');
const selectedRole = ref('');
const hasBankAccount = ref(false);
const isExpanded = ref(false);
const roles = [
  { value: '', label: 'All Roles' },
  { value: 'admin', label: 'Admin' },
  { value: 'customer', label: 'Customer' },
  { value: 'delivery_driver', label: 'Delivery Driver' },
  { value: 'business', label: 'Business' }
];

const emit = defineEmits(['search', 'reset']);

const hasActiveFilters = computed(() => {
  return search.value !== '' || selectedRole.value !== '' || hasBankAccount.value;
});

const activeFilterCount = computed(() => {
  let count = 0;
  if (search.value) count++;
  if (selectedRole.value) count++;
  if (hasBankAccount.value) count++;
  return count;
});

const handleSearch = () => {
  const filterObject = {
    search: search.value,
    selectedRole: selectedRole.value,
    hasBankAccount: hasBankAccount.value
  };

  emit('search', filterObject);
};

const handleReset = () => {
  search.value = '';
  selectedRole.value = '';
  hasBankAccount.value = false;
  emit('search', null);
};


</script>

<style lang="scss" scoped></style>
